import { useEffect, useMemo, useRef, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { FilePdf, ArrowSquareOut, DownloadSimple, Spinner, Warning, UploadSimple, X } from "@phosphor-icons/react";

export const PDF_DOC_TYPES = [
  { key: "quote", label: "Quote" },
  { key: "invoice", label: "Invoice" },
  { key: "works_order", label: "Works order" },
  { key: "job_sheet", label: "Job sheet" },
  { key: "completion_report", label: "Completion report" },
  { key: "in_house_po", label: "In-house PO" },
];

// Live sample document rendered with the tenant's (unsaved) branding + the document strapline / footer fields.
// Page 1 is shown as an image (works in every browser); Open / Download fetch the real PDF.
export function PdfBrandingCard({ companyName, primaryColor, logoUrl, tagline, footerText, pdfLogoUrl = "", uploadingPdfLogo = false, onPdfLogoUpload, onPdfLogoRemove, hidePlatformBranding, onTaglineChange, onFooterTextChange, savedAt }) {
  const [doc, setDoc] = useState("quote");
  const [imgUrl, setImgUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingPdf, setFetchingPdf] = useState("");
  const [error, setError] = useState("");
  const timer = useRef(null);

  const params = useMemo(() => {
    const p = new URLSearchParams({ doc });
    if (companyName) p.set("company_name", companyName);
    if (primaryColor) p.set("primary_color", primaryColor);
    if (logoUrl) p.set("logo_url", logoUrl);
    if (tagline) p.set("tagline", tagline);
    if (footerText) p.set("footer_text", footerText);
    p.set("pdf_logo_url", (pdfLogoUrl || "").split("?")[0]);
    p.set("hide_platform_branding", hidePlatformBranding ? "1" : "0");
    return p.toString();
  }, [doc, companyName, primaryColor, logoUrl, tagline, footerText, pdfLogoUrl, hidePlatformBranding]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await API.get(`/tenant/branding/pdf-preview?${params}&format=png`, { responseType: "blob" });
        setImgUrl(URL.createObjectURL(data));
      } catch (e) {
        setError(e?.response?.data?.detail || "Preview unavailable");
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [params, savedAt]);

  useEffect(() => () => { if (imgUrl) URL.revokeObjectURL(imgUrl); }, [imgUrl]);

  const fetchPdf = async (mode) => {
    setFetchingPdf(mode);
    try {
      const { data } = await API.get(`/tenant/branding/pdf-preview?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (mode === "open") {
        window.open(url, "_blank", "noopener");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `sample-${doc}.pdf`;
        a.click();
        toast.success("Sample PDF downloaded");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the sample PDF");
    } finally {
      setFetchingPdf("");
    }
  };

  return (
    <div className="mt-8 rounded-xl border border-border bg-card p-5 sm:p-6" data-testid="pdf-branding-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <FilePdf size={18} className="text-primary" /> Document branding
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Quotes, invoices, works orders, in-house POs, job sheets and completion reports all carry your logo, accent colour, strapline and footer. The sample below re-renders as you edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchPdf("open")} disabled={!!fetchingPdf} data-testid="pdf-preview-open-btn">
            {fetchingPdf === "open" ? <Spinner size={14} className="animate-spin mr-1" /> : <ArrowSquareOut size={14} className="mr-1" />} Open PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchPdf("download")} disabled={!!fetchingPdf} data-testid="pdf-preview-download-btn">
            {fetchingPdf === "download" ? <Spinner size={14} className="animate-spin mr-1" /> : <DownloadSimple size={14} className="mr-1" />} Download sample
          </Button>
        </div>
      </div>

      <div className="inline-flex flex-wrap rounded-md border border-border overflow-hidden text-xs mb-4" data-testid="pdf-preview-doc-tabs">
        {PDF_DOC_TYPES.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => setDoc(d.key)}
            className={`px-3 py-1.5 transition-colors ${doc === d.key ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            data-testid={`pdf-preview-tab-${d.key}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-lg border border-border bg-[#e5e7eb] dark:bg-[#111] overflow-hidden relative min-h-[420px] flex items-start justify-center p-4" data-testid="pdf-preview-frame-wrap">
          {loading && (
            <div className="absolute top-2 right-2 z-10 text-[10px] text-muted-foreground bg-background/90 px-2 py-0.5 rounded flex items-center gap-1" data-testid="pdf-preview-loading">
              <Spinner size={12} className="animate-spin" /> Rendering…
            </div>
          )}
          {imgUrl && !error ? (
            <img src={imgUrl} alt={`Sample ${doc.replace("_", " ")} — page 1`} className="max-h-[720px] w-auto max-w-full shadow-lg rounded-sm bg-white" data-testid="pdf-preview-image" />
          ) : (
            <div className="p-8 text-sm text-muted-foreground flex items-center gap-2" data-testid="pdf-preview-error">
              {error ? <><Warning size={16} /> {error}</> : <><Spinner size={16} className="animate-spin" /> Rendering sample…</>}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">PDF logo (optional)</Label>
            <div className="flex items-center gap-3 mt-1">
              {pdfLogoUrl ? (
                <div className="h-12 w-28 rounded border border-border flex items-center justify-center bg-white overflow-hidden shrink-0" data-testid="pdf-logo-preview">
                  <img src={pdfLogoUrl} alt="PDF logo" className="max-h-10 max-w-[104px] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              ) : (
                <div className="h-12 w-28 rounded border-2 border-dashed border-border flex items-center justify-center bg-muted/30 shrink-0 text-[10px] text-muted-foreground" data-testid="pdf-logo-empty">
                  Uses app logo
                </div>
              )}
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <label>
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={uploadingPdfLogo}
                      onChange={(e) => { onPdfLogoUpload && onPdfLogoUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="pdf-logo-upload-input" />
                    <Button variant="outline" size="sm" asChild disabled={uploadingPdfLogo}>
                      <span className="cursor-pointer" data-testid="pdf-logo-upload-btn">
                        {uploadingPdfLogo ? <><Spinner size={14} className="mr-1.5 animate-spin" />Uploading…</> : <><UploadSimple size={14} className="mr-1.5" />{pdfLogoUrl ? "Replace" : "Upload PDF logo"}</>}
                      </span>
                    </Button>
                  </label>
                  {pdfLogoUrl && (
                    <Button variant="ghost" size="sm" onClick={onPdfLogoRemove} data-testid="pdf-logo-remove-btn"><X size={14} className="mr-1" /> Use app logo</Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">Wide / print version for documents when your app logo is a square icon. PNG, JPEG or WebP, max 5MB — the app logo is used when empty.</p>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="pdf-tagline" className="text-xs">Strapline (optional)</Label>
            <Input
              id="pdf-tagline"
              value={tagline}
              onChange={(e) => onTaglineChange(e.target.value.slice(0, 120))}
              placeholder="Facilities maintenance across Yorkshire"
              className="mt-1"
              data-testid="pdf-tagline-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Sits under your company name in the document header.</p>
          </div>
          <div>
            <Label htmlFor="pdf-footer" className="text-xs">Document footer (optional)</Label>
            <Textarea
              id="pdf-footer"
              value={footerText}
              onChange={(e) => onFooterTextChange(e.target.value.slice(0, 400))}
              placeholder={"Acme Facilities Ltd · Registered in England 01234567\nVAT GB 123 4567 89 · 1 Example Street, Leeds LS1 1AA"}
              rows={4}
              className="mt-1 text-sm"
              data-testid="pdf-footer-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">One line per row — company registration, VAT number, registered address, payment terms. {footerText.length}/400. Saved with the button above.</p>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
            <li>Your accent colour drives headings, rules and table headers on every document.</li>
            <li>Clients with their own white-label logo keep it on client-facing PDFs; otherwise the PDF logo (or app logo) is used.</li>
            <li>{hidePlatformBranding ? "\u201cPowered by Fixlr\u201d is hidden (White-label add-on)." : "A small \u201cPowered by Fixlr\u201d line closes each document \u2014 the White-label add-on removes it."}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
